/** Two-dimensional vectors, as plain data. The optics engine's own — see types.ts. */
import { Vec2 } from './types';

export const v = (x: number, y: number): Vec2 => ({ x, y });
export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k });
export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
/** z of the 3D cross product: positive when b is anticlockwise of a. */
export const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
export const len = (a: Vec2): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
/** Rotated a quarter turn anticlockwise. */
export const perp = (a: Vec2): Vec2 => ({ x: -a.y, y: a.x });
export const neg = (a: Vec2): Vec2 => ({ x: -a.x, y: -a.y });

export const unit = (a: Vec2): Vec2 => {
    const L = len(a);
    return L < 1e-15 ? { x: 0, y: 0 } : { x: a.x / L, y: a.y / L };
};

export const fromAngle = (theta: number): Vec2 => ({ x: Math.cos(theta), y: Math.sin(theta) });
export const rotate = (a: Vec2, theta: number): Vec2 => {
    const c = Math.cos(theta), s = Math.sin(theta);
    return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
};

/** A point in a part's own frame (+x along the way it faces) to bench coordinates. */
export const toWorld = (at: Vec2, angle: number, local: Vec2): Vec2 => add(at, rotate(local, angle));

export const deg = (rad: number): number => (rad * 180) / Math.PI;
export const rad = (d: number): number => (d * Math.PI) / 180;

/** Angle between two unit vectors, degrees, 0–180. */
export const angleBetween = (a: Vec2, b: Vec2): number =>
    deg(Math.acos(Math.max(-1, Math.min(1, dot(unit(a), unit(b))))));
